import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AutoAwesomeOutlined } from '@mui/icons-material';
import { InkyMascot, InkyWorking } from '../brand';
import aiService from '../../services/aiService';
import { AI_TASKS, stableSeed } from '../../constants/aiModels';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/EnterpriseAuthContext';
import {
  INKY_TEMPLATE_SCHEMA,
  INKY_TEMPLATE_SYSTEM,
  buildInkyTemplatePrompt,
  buildTemplateFromOutline,
} from './templateHelpers';

const EXAMPLES = ['Statement of work', 'Weekly status report', 'Functional design document', 'Kickoff meeting summary'];

/** Most descriptions remembered by {@link nextVariantFor}; the oldest is dropped beyond this. */
const MAX_REMEMBERED_DESCRIPTIONS = 50;

/**
 * Next seed variant per exact description, kept for the browser session (module scope, so it
 * survives closing and reopening the dialog). Drafting the same description again then gives a
 * different template on purpose, while a first draft stays reproducible (variant 0).
 * @type {Map<string, number>}
 */
const variantsByDescription = new Map();

/**
 * Variant to use for the next draft of a description.
 * @param {string} prompt - Built prompt (the full description).
 * @returns {number} Variant (0 for a description not drafted yet).
 */
const nextVariantFor = (prompt) => variantsByDescription.get(prompt) || 0;

/**
 * Advance the variant for a description past `used`.
 * @param {string} prompt - Built prompt.
 * @param {number} used - Variant just used or skipped.
 * @returns {void}
 */
const advanceVariant = (prompt, used) => {
  variantsByDescription.delete(prompt);
  variantsByDescription.set(prompt, used + 1);
  if (variantsByDescription.size > MAX_REMEMBERED_DESCRIPTIONS) {
    variantsByDescription.delete(variantsByDescription.keys().next().value);
  }
};

/**
 * "Create with Inky": describe a document, Inky drafts a template (headings, fixed text and one
 * AI field per section), it is saved as a new template and opened in the editor for review.
 * @param {object} props - Props.
 * @param {boolean} props.open - Visibility.
 * @param {function(): void} props.onClose - Close.
 * @param {function(object): void} props.onCreated - Called with the saved template.
 * @returns {JSX.Element} Dialog.
 */
export default function CreateWithInkyDialog({ open, onClose, onCreated }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { actions } = useApp();
  const { state: authState } = useAuth();
  const [purpose, setPurpose] = useState('');
  const [sections, setSections] = useState('');
  const [audience, setAudience] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const abortRef = useRef(null);
  const prompt = useMemo(() => buildInkyTemplatePrompt({ purpose, sections, audience }), [purpose, sections, audience]);
  const draftedBefore = Boolean(purpose.trim()) && nextVariantFor(prompt) > 0;

  useEffect(() => () => abortRef.current?.abort(), []);

  const reset = () => {
    setPurpose('');
    setSections('');
    setAudience('');
    setError('');
  };

  const handleClose = () => {
    if (busy) {
      abortRef.current?.abort();
      return;
    }
    reset();
    onClose();
  };

  const handleCreate = async ({ newVariant = false } = {}) => {
    if (!purpose.trim()) return;
    // "Try another version" after a failed draft skips the variant that just failed.
    if (newVariant) advanceVariant(prompt, nextVariantFor(prompt));
    const variant = nextVariantFor(prompt);
    setBusy(true);
    setError('');
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const result = await aiService.runTask({
        task: AI_TASKS.TEMPLATE_ANALYZE,
        profile: 'extract',
        seed: stableSeed('template.create', prompt, variant),
        systemInstruction: INKY_TEMPLATE_SYSTEM,
        prompt,
        responseSchema: INKY_TEMPLATE_SCHEMA,
        accountId: authState.activeAccount?.id,
        signal: controller.signal,
      });
      const draft = buildTemplateFromOutline(result.json);
      const saved = await actions.createTemplate({
        name: draft.name,
        description: draft.description || `Drafted by Inky: ${purpose.trim()}`,
        content: draft.content,
      });
      if (!saved || saved.success === false) throw new Error(saved?.error || 'Could not save the template.');
      advanceVariant(prompt, variant);
      reset();
      onCreated(saved.data);
    } catch (err) {
      if (controller.signal.aborted) {
        setError('Stopped. Your description is kept.');
      } else {
        console.error('Create with Inky failed:', err);
        setError(err?.message || 'Inky could not draft this template. Please try again.');
      }
    } finally {
      abortRef.current = null;
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth fullScreen={fullScreen} aria-labelledby="create-with-inky-title">
      <DialogTitle id="create-with-inky-title" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <InkyMascot size={36} mood={busy ? 'writing' : 'idle'} decorative />
        <Box>
          <Typography component="span" sx={{ display: 'block', fontSize: 17, fontWeight: 600 }}>Create a template with Inky</Typography>
          <Typography component="span" variant="body2" color="text.secondary">
            Describe the document. Inky drafts the sections and AI fields; you review it in the editor.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField
          autoFocus
          required
          multiline
          minRows={2}
          maxRows={6}
          label="What document should this template produce?"
          placeholder="e.g. A statement of work for software implementation projects, with scope, deliverables, timeline and acceptance criteria"
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          disabled={busy}
          inputProps={{ maxLength: 1500 }}
        />
        {!purpose && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: -1 }} aria-label="Examples">
            {EXAMPLES.map((example) => (
              <Button key={example} size="small" variant="outlined" onClick={() => setPurpose(example)} sx={{ borderRadius: 999 }}>
                {example}
              </Button>
            ))}
          </Box>
        )}
        <TextField
          multiline
          minRows={2}
          maxRows={8}
          label="Sections to include (optional)"
          placeholder={'One per line, e.g.\nExecutive summary\nScope\nRisks'}
          value={sections}
          onChange={(e) => setSections(e.target.value)}
          disabled={busy}
          inputProps={{ maxLength: 2000 }}
        />
        <TextField
          label="Audience (optional)"
          placeholder="e.g. Client stakeholders"
          value={audience}
          onChange={(e) => setAudience(e.target.value)}
          disabled={busy}
          inputProps={{ maxLength: 200 }}
        />
        {draftedBefore && !busy && !error && (
          <Typography variant="caption" color="text.secondary">
            Inky already drafted a template from this description, so this draft will be a different version.
          </Typography>
        )}
        {busy && <InkyWorking label="Inky is drafting your template" mood="writing" />}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }} disableSpacing>
        <Button onClick={handleClose}>{busy ? 'Stop' : 'Cancel'}</Button>
        {error && !busy && purpose.trim() && (
          <Button variant="outlined" onClick={() => handleCreate({ newVariant: true })}>Try another version</Button>
        )}
        <Button
          variant="contained"
          startIcon={<AutoAwesomeOutlined />}
          onClick={() => handleCreate()}
          disabled={!purpose.trim() || busy}
        >
          Draft template
        </Button>
      </DialogActions>
    </Dialog>
  );
}
